/**
 * spiritualJourneyMemory.js
 *
 * Long-horizon spiritual state storage.
 * Distinct from relationalMemoryEngine (7-day visit tones) — this stores
 * SPIRITUAL PROCESS STATES across weeks and months.
 *
 * What gets stored: the spiritual process state the person was in when
 * the pastor discussed them with Kevia. Not conversations. Signals.
 *
 * Examples of what builds up over time:
 *  - Person spent 3 sessions in "quebranto" → pattern of brokenness
 *  - Family oscillated búsqueda → confusión → búsqueda → restoration emerges
 *  - Pastor noticed agotamiento_espiritual 4 times in 6 weeks → persistent exhaustion
 *
 * Storage: localStorage — key per entity, 60-day TTL, max 20 entries per entity.
 * Key prefix distinct from relationalMemoryEngine to avoid collisions.
 */

const KEY_PREFIX = "kevia_sjm_v1_";
const ENTRY_TTL  = 60 * 24 * 60 * 60 * 1000;  // 60 days
const MAX_ENTRIES_PER_ENTITY = 20;
const MAX_ENTITIES = 60;

/**
 * @typedef {{
 *   ts:           number,        — Unix ms
 *   processState: string,        — from spiritualProcessDiscernment
 *   tone:         string,        — entity tone at time of recording
 *   intensity:    "low"|"medium"|"high",
 *   note:         string|null,   — optional compact note
 * }} JourneyEntry
 */

/**
 * @typedef {{
 *   entityId:   string,
 *   entityType: string,
 *   name:       string,
 *   entries:    JourneyEntry[],
 *   firstSeen:  number,
 *   lastSeen:   number,
 * }} JourneyRecord
 */

function _key(entityId) {
  return KEY_PREFIX + entityId;
}

function _allKeys() {
  const keys = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k?.startsWith(KEY_PREFIX)) keys.push(k);
  }
  return keys;
}

/**
 * Load the journey record for an entity, pruned by TTL.
 * @param {string} entityId
 * @returns {JourneyRecord|null}
 */
export function getJourneyHistory(entityId) {
  if (!entityId) return null;
  try {
    const raw = localStorage.getItem(_key(entityId));
    if (!raw) return null;
    const record = JSON.parse(raw);
    const now    = Date.now();
    record.entries = (record.entries ?? []).filter((e) => now - e.ts < ENTRY_TTL);
    return record.entries.length > 0 ? record : null;
  } catch {
    return null;
  }
}

/**
 * Record a spiritual process state observed during a pastoral conversation.
 * Deduplicates within a 2-hour window (same session multiple renders).
 *
 * @param {string} entityId
 * @param {string} entityType
 * @param {string} name
 * @param {JourneyEntry} entry
 */
export function recordJourneyEntry(entityId, entityType, name, entry) {
  if (!entityId || !entry?.processState) return;
  // Don't store baseline "apertura" — only meaningful spiritual states
  if (entry.processState === "apertura") return;

  try {
    _pruneOverflow();
    const existing = getJourneyHistory(entityId);
    const prev     = existing ?? {
      entityId, entityType, name, entries: [], firstSeen: Date.now(), lastSeen: 0,
    };

    // Deduplicate within 2 hours + same state
    const last = prev.entries[0];
    if (last && (entry.ts - last.ts) < 2 * 60 * 60 * 1000 && last.processState === entry.processState) {
      return;
    }

    const updated = {
      ...prev,
      name,
      entityType,
      lastSeen: entry.ts,
      entries: [{ ...entry }, ...prev.entries].slice(0, MAX_ENTRIES_PER_ENTITY),
    };

    localStorage.setItem(_key(entityId), JSON.stringify(updated));
  } catch {
    // localStorage quota — silent fail
  }
}

/**
 * Returns how many days span the full journey history for an entity.
 * Returns 0 if no history.
 * @param {JourneyRecord|null} record
 * @returns {number}
 */
export function getJourneySpanDays(record) {
  if (!record?.entries?.length) return 0;
  const entries = record.entries;
  if (entries.length < 2) return 0;
  const newest = entries[0].ts;
  const oldest = entries[entries.length - 1].ts;
  return Math.round((newest - oldest) / 86_400_000);
}

/**
 * Returns the most recent N entries, optionally filtered by state.
 * @param {JourneyRecord|null} record
 * @param {{ limit?: number, state?: string }} opts
 * @returns {JourneyEntry[]}
 */
export function getRecentEntries(record, { limit = 5, state = null } = {}) {
  if (!record?.entries?.length) return [];
  const entries = record.entries;
  const filtered = state ? entries.filter((e) => e.processState === state) : entries;
  return filtered.slice(0, limit);
}

/**
 * Delete the journey record for a specific entity.
 * @param {string} entityId
 */
export function clearJourneyHistory(entityId) {
  if (!entityId) return;
  try { localStorage.removeItem(_key(entityId)); } catch { /* ignore */ }
}

// ── Private ───────────────────────────────────────────────────────────────────

function _pruneOverflow() {
  const keys = _allKeys();
  if (keys.length < MAX_ENTITIES) return;
  const withTs = keys.map((k) => {
    try {
      const r = JSON.parse(localStorage.getItem(k) ?? "{}");
      return { key: k, lastSeen: r.lastSeen ?? 0 };
    } catch {
      return { key: k, lastSeen: 0 };
    }
  });
  withTs.sort((a, b) => a.lastSeen - b.lastSeen);
  const toRemove = withTs.slice(0, withTs.length - MAX_ENTITIES + 1);
  for (const { key } of toRemove) {
    try { localStorage.removeItem(key); } catch { /* ignore */ }
  }
}
